// noinspection ExceptionCaughtLocallyJS

import { SignPrivateKeyStrategy } from '@/sign/strategies/sign-private-key.strategy';
import { isSealCertificates } from '@/sign/helpers/sign.helper';
import { getLocale } from '@/helpers';
import { EventBus } from '@/helpers/event-bus';
import { hideLoading, IAppLoaderTextOptions, isLoadingVisible, setLoadingCancelCallback, showLoading } from '@/mixins/ui-mixin';
import { executeCloudSignOperationWithConfirmationByQrCode } from '@/sign/helpers/cloud-sign-qr.helper';
import { AppSignKeyOperationType } from '@/sign/models/sign-constants';

type Constructor<T> = new (...args: any[]) => T;

export function MixWithFillEndUserOwnerInfoFromSigns<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends Base {
    protected async fillEndUserOwnerInfoFromSigns(
      signs: Array<any>,
      getSignFnc: (sign: any) => string = (s) => (s ? s.signature : '')) {
      if (signs && signs.length > 0) {
        const sign = getSignFnc(signs[0]);
        let certs = await this.euSignLib.GetSigner(sign, -1, false);
        if (certs && certs.length) {
          let endUserOwnerInfo = certs[0].infoEx;
          let signingParamsData = this.signingParams.data || {};
          signingParamsData.endUserOwnerInfo = endUserOwnerInfo;
          this.signingParams.data = signingParamsData;
        }
      }
    }
  };
}

export function MixWithDefaultCloudDocumentSigning<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends Base {
    protected isKeyPrivateKeyReaded(): Promise<boolean> {
      return this.euSignLib.IsPrivateKeyReaded();
    }

    protected getOwnCertificates(): Promise<any> {
      return this.euSignLib.GetOwnCertificates();
    }

    protected async validateSigningKeyType(lib: any, signResult: string): Promise<void> {
      let readCerts = this.signingParams.data.ownCertificates;
      let readCertSerial = readCerts && readCerts.length > 0 ? readCerts[0].infoEx.serial : '-1';
      let signCerts = await lib.GetSigner(signResult, -1, false);
      let signCertSerial = signCerts && signCerts.length > 0 ? signCerts[0].infoEx.serial : '-2';

      if (this.signingParams.validateKeyType) {
        if (!this.isSeal && isSealCertificates(signCerts)) {
          throw new Error(getLocale('appSign.errors.selectedKeyIsNotSigningKey').toString());
        } else if (this.isSeal && !isSealCertificates(signCerts)) {
          throw new Error(getLocale('appSign.errors.selectedKeyIsNotSeal').toString());
        }
      }

      if (readCertSerial !== signCertSerial) {
        throw new Error(getLocale('appSign.errors.differentKeyTypeOnSigning').toString());
      }
    }
  };
}

export function MixWithDefaultQrCodeDocumentSigning<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends MixWithDefaultCloudDocumentSigning(Base) {
    protected async signHash(
      lib: any,
      signAlgo: number,
      hash: string | Uint8Array | Array<string | Uint8Array>,
      appendCert: boolean,
      asBase64String: boolean): Promise<any> {
      return this.signWrapper(lib, 'SignHash', [signAlgo, hash, appendCert, asBase64String]);
    }

    protected async signData(
      lib: any,
      signAlgo: number,
      data: string | Uint8Array | Array<string | Uint8Array>,
      external: boolean,
      appendCert: boolean,
      asBase64String: boolean): Promise<any> {
      let operationName = external ? 'SignData' : 'SignDataInternal';
      let operationParams = external ? [data, asBase64String] : [appendCert, data, asBase64String];
      return this.signWrapper(lib, operationName, operationParams);
    }

    protected async signWrapper(lib: any, operationName: string, operationParams: any[]): Promise<any> {
      try {
        const signResult = await executeCloudSignOperationWithConfirmationByQrCode(this.signingParams.signProvider, lib, operationName,
          operationParams, this.signingParams.privateKeyType, AppSignKeyOperationType.Signing, this.mobileAppName);

        await this.validateSigningKeyType(lib, signResult);

        return signResult;
      } catch (error) {
        if (/cancelled/i.test(error as any)) {
          EventBus.$emit('app-sign-reset-private-key', {
            privateKeyType: this.signingParams?.privateKeyType
          });
        }
        throw error;
      }
    }
  };
}

export function MixWithDefaultAppConfirmationDocumentSigning<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends MixWithDefaultCloudDocumentSigning(Base) {
    protected async signHash(
      lib: any,
      signAlgo: number,
      hash: string | Uint8Array | Array<string | Uint8Array>,
      appendCert: boolean,
      asBase64String: boolean): Promise<any> {
      let signFn = () => {
        return lib.SignHash(signAlgo, hash, appendCert, asBase64String);
      };
      return this.signWrapper(lib, signFn);
    }

    protected async signData(
      lib: any,
      signAlgo: number,
      data: string | Uint8Array | Array<string | Uint8Array>,
      external: boolean,
      appendCert: boolean,
      asBase64String: boolean): Promise<any> {
      let signFn = () => {
        return external ? lib.SignData(data, asBase64String) : lib.SignDataInternal(appendCert, data, asBase64String);
      };
      return this.signWrapper(lib, signFn);
    }

    private async signWrapper(lib: any, signFn: () => Promise<any>): Promise<any> {
      let msg = this.isSeal ? 'appSignCloudProviderDefault.selectSealInMobileApp' : 'appSignCloudProviderDefault.selectKeyInMobileApp';

      let isLoaderVis = isLoadingVisible();
      if (isLoaderVis) {
        await hideLoading();
      }

      const loaderOpts = {
        text: getLocale(msg, { mobileAppName: this.mobileAppName }).toString()
      } as IAppLoaderTextOptions;

      showLoading({}, true, loaderOpts);

      setLoadingCancelCallback(() => { lib.ResetOperationKSP(); });

      try {
        let signResult = await signFn();

        await this.validateSigningKeyType(lib, signResult);

        return signResult;
      } finally {
        setLoadingCancelCallback(null);

        await hideLoading();

        if (isLoaderVis) {
          showLoading({}, true);
        }
      }
    }
  };
}
